import { reapModulators } from '~/lib/automation/model'
import { reapSourceSettings } from '~/lib/params'

const logger = console.scope('live-utils', true)

/* Collect `ids` plus every descendant. Copy and delete must agree on what a subtree IS — when they disagreed (delete recursed, copy did not) cutting a group deleted the whole subtree while the clipboard held only the group node. Returns ids, not objects; the caller decides what to do with them.

Ordering is load-bearing: a parent is always emitted before its children, because addObjects pushes each object into its parent's `children` and a child arriving first would find no parent and be re-homed to root — the group would silently paste short. Selecting a child and its own ancestor puts the child in `ids` first, so roots are sorted by depth before walking rather than taken in selection order. */
export const gatherSubtreeIds = (objects, ids) => {
	const collected = new Set()
	if (!ids?.length) return collected

	const depthOf = id => {
		let d = 0
		let cur = objects[id]
		while (cur?.parentId && objects[cur.parentId] && d < 100) {
			d++
			cur = objects[cur.parentId]
		}
		return d
	}

	const walk = id => {
		if (collected.has(id)) return
		const obj = objects[id]
		if (!obj) return
		collected.add(id)
		obj.children?.forEach(walk)
	}

	;[...ids].sort((a, b) => depthOf(a) - depthOf(b)).forEach(walk)
	return collected
}

export const recursivelyDeleteObjects = (live, ids) => {
	if (!ids) {
		logger.error('deleteObjects: No ids provided')
		return
	}

	const { objects } = live
	const idsToRemove = new Set()
	let node

	const gatherChildren = obj => {
		idsToRemove.add(obj.id)
		if (obj.mask) {
			node = objects[obj.mask]
			if (node) delete node.maskFor
		} else if (obj.maskFor) {
			node = objects[obj.maskFor]
			if (node) delete node.mask
		}
		if (obj.children) {
			for (const childId of obj.children) {
				const child = objects[childId]
				if (child) gatherChildren(child)
			}
		}
	}

	const gatherParents = obj => {
		if (obj.parentId) {
			const parent = objects[obj.parentId]
			parent.children = parent.children.filter(child => child !== obj.id)
			if (parent.id !== 'root' && !parent.children.length) {
				idsToRemove.add(parent.id)
				gatherParents(parent)
			}
		}
	}

	ids.forEach(id => {
		// A lock guards against stage edits, not an explicit delete; tools that must spare locked shapes (the eraser) filter them first.
		const obj = objects[id]
		if (!obj) return
		gatherChildren(obj)
		gatherParents(obj)
	})

	idsToRemove.forEach(id => delete objects[id])
	reapModulators(live)
	reapSourceSettings(live)
	window.removed_object_ids = idsToRemove

	// live.selected_object_ids = live.selected_object_ids.filter(f => !idsToRemove.has(f)) expanded ids are updated in syncObjects middleware
}
